/**
 * 스타일 마이그레이션 헬퍼
 * 레거시 스타일 속성을 표준 스타일 구조로 변환하고 관리
 */

/**
 * 레거시 스타일 매핑 정의
 * 각 차트 타입별로 레거시 속성 경로와 표준 경로를 매핑
 */
const LEGACY_STYLE_MAPPINGS = {
  badge: {
    // 제목 관련
    'titleFontSize': { standard: 'style.fonts.title.fontSize', legacy: ['advanced.titleFontSize', 'titleFontSize'] },
    'titleColor': { standard: 'style.fonts.title.color', legacy: ['advanced.titleColor', 'titleColor'] },
    'titleFontWeight': { standard: 'style.fonts.title.fontWeight', legacy: ['advanced.titleFontWeight'] },
    
    // 값 관련
    'fontSize': { standard: 'style.fonts.value.fontSize', legacy: ['advanced.fontSize', 'fontSize', 'valueFontSize', 'advanced.valueFontSize'] },
    'textColor': { standard: 'style.fonts.value.color', legacy: ['advanced.textColor', 'textColor'] },
    'fontWeight': { standard: 'style.fonts.value.fontWeight', legacy: ['advanced.fontWeight'] },
    
    // 단위 관련
    'unitFontSize': { standard: 'style.fonts.unit.fontSize', legacy: ['advanced.unitFontSize', 'unitFontSize'] },
    'unitColor': { standard: 'style.fonts.unit.color', legacy: ['advanced.unitColor'] },
    
    // 배경 관련
    'badgeColor': { standard: 'style.background.color', legacy: ['advanced.badgeColor', 'badgeColor'] },
    'badgeStyle': { standard: 'style.background.shape', legacy: ['advanced.badgeStyle', 'badgeStyle'] }
  },
  gauge: {
    // 게이지도 배지와 유사한 구조
    'titleFontSize': { standard: 'style.fonts.title.fontSize', legacy: ['advanced.titleFontSize', 'titleFontSize'] },
    'fontSize': { standard: 'style.fonts.value.fontSize', legacy: ['advanced.fontSize', 'fontSize', 'valueFontSize'] },
    'textColor': { standard: 'style.fonts.value.color', legacy: ['advanced.textColor', 'textColor'] },
    'unitFontSize': { standard: 'style.fonts.unit.fontSize', legacy: ['advanced.unitFontSize', 'unitFontSize'] }
  },
  text_label: {
    'fontSize': { standard: 'style.fonts.value.fontSize', legacy: ['fontSize', 'baseFontSize'] },
    'fontWeight': { standard: 'style.fonts.value.fontWeight', legacy: ['fontWeight'] },
    'fontStyle': { standard: 'style.fonts.value.fontStyle', legacy: ['fontStyle'] },
    'textColor': { standard: 'style.fonts.value.color', legacy: ['textColor'] },
    'fontFamily': { standard: 'style.fonts.value.fontFamily', legacy: ['fontFamily'] },
    'lineHeight': { standard: 'style.fonts.value.lineHeight', legacy: ['lineHeight'] },
    'textAlign': { standard: 'style.fonts.value.textAlign', legacy: ['textAlign'] },
    'backgroundColor': { standard: 'style.background.color', legacy: ['backgroundColor'] },
    'borderRadius': { standard: 'style.background.borderRadius', legacy: ['borderRadius'] }
  }
};

/**
 * 중첩된 객체에서 경로로 값 가져오기
 */
const getNestedValue = (obj, path) => {
  if (!obj || !path) return undefined;
  
  const keys = path.split('.');
  let value = obj;
  
  for (const key of keys) {
    if (value?.[key] !== undefined) {
      value = value[key];
    } else {
      return undefined;
    }
  }
  
  return value;
};

/**
 * 중첩된 객체에 경로로 값 설정하기
 */
const setNestedValue = (obj, path, value) => {
  const keys = path.split('.');
  let current = obj;
  
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (!current[key]) {
      current[key] = {};
    }
    current = current[key];
  }
  
  current[keys[keys.length - 1]] = value;
};

/**
 * 레거시 스타일 속성을 표준 구조로 마이그레이션
 * @param {Object} options - 차트 옵션 객체
 * @param {string} chartType - 차트 타입 (badge, gauge, text_label 등)
 * @returns {Object} 마이그레이션된 옵션 객체
 */
export const migrateStyleToStandard = (options, chartType) => {
  if (!options) return { style: { fonts: {}, background: {} } };
  
  const mappings = LEGACY_STYLE_MAPPINGS[chartType] || {};
  const migratedOptions = JSON.parse(JSON.stringify(options)); // 깊은 복사
  
  // 표준 style 구조 초기화
  if (!migratedOptions.style) {
    migratedOptions.style = { fonts: {}, background: {} };
  }
  if (!migratedOptions.style.fonts) {
    migratedOptions.style.fonts = {};
  }
  if (!migratedOptions.style.background) {
    migratedOptions.style.background = {};
  }
  
  // 각 매핑에 대해 처리
  Object.entries(mappings).forEach(([key, mapping]) => {
    // 표준 경로에 이미 값이 있으면 건너뛰기
    const standardPath = mapping.standard.replace('style.', '');
    const existingValue = getNestedValue(migratedOptions.style, standardPath);
    
    if (existingValue === undefined || existingValue === null) {
      // 레거시 경로들에서 값 찾기
      for (const legacyPath of mapping.legacy) {
        const legacyValue = getNestedValue(migratedOptions, legacyPath);
        if (legacyValue !== undefined && legacyValue !== null) {
          // 값 타입에 따라 변환
          let processedValue = legacyValue;
          
          // fontSize 관련 필드는 px 제거하고 숫자로 변환
          if (key.includes('FontSize') && typeof legacyValue === 'string') {
            processedValue = parseInt(legacyValue.replace('px', ''));
          }
          // borderRadius도 px 제거하고 숫자로 변환
          else if (key === 'borderRadius' && typeof legacyValue === 'string') {
            processedValue = parseInt(legacyValue.replace('px', ''));
          }
          // lineHeight는 숫자 타입으로 변환
          else if (key === 'lineHeight' && typeof legacyValue === 'string') {
            processedValue = parseFloat(legacyValue);
          }
          
          // 표준 경로에 값 설정
          setNestedValue(migratedOptions.style, standardPath, processedValue);
          break; // 첫 번째 유효한 값을 찾으면 중단
        }
      }
    }
  });
  
  // 레거시 속성 제거
  const cleanupLegacyProperties = (opts) => {
    // advanced 속성에서 스타일 관련 속성 제거
    if (opts.advanced) {
      const styleKeys = [
        'titleFontSize', 'titleColor', 'titleFontWeight',
        'fontSize', 'valueFontSize', 'textColor', 'fontWeight',
        'unitFontSize', 'unitColor',
        'badgeColor', 'badgeStyle'
      ];
      
      styleKeys.forEach(key => {
        if (opts.advanced[key] !== undefined) {
          delete opts.advanced[key];
        }
      });
      
      // advanced가 비어있으면 제거
      if (Object.keys(opts.advanced).length === 0) {
        delete opts.advanced;
      }
    }
    
    // 최상위 레벨의 스타일 속성 제거
    const topLevelStyleKeys = [
      'fontSize', 'valueFontSize', 'textColor', 'fontWeight',
      'titleFontSize', 'titleColor',
      'unitFontSize', 'unitColor',
      'badgeColor', 'badgeStyle',
      'fontFamily', 'fontStyle', 'lineHeight', 'textAlign',
      'backgroundColor', 'borderRadius'
    ];
    
    topLevelStyleKeys.forEach(key => {
      if (opts[key] !== undefined) {
        delete opts[key];
      }
    });
    
    return opts;
  };
  
  // 레거시 속성은 호환성을 위해 보존
  return migratedOptions;
};

/**
 * 스타일 값 가져오기 (레거시 폴백 포함)
 * @param {Object} options - 차트 옵션
 * @param {string} chartType - 차트 타입
 * @param {string} styleKey - 스타일 키 (예: 'titleFontSize')
 * @returns {any} 스타일 값
 */
export const getStyleValue = (options, chartType, styleKey) => {
  if (!options) return undefined;
  
  const mapping = LEGACY_STYLE_MAPPINGS[chartType]?.[styleKey];
  if (!mapping) return undefined;
  
  // 1. 표준 경로에서 먼저 찾기
  const standardPath = mapping.standard.replace('style.', '');
  const standardValue = getNestedValue(options.style, standardPath);
  if (standardValue !== undefined && standardValue !== null) {
    return standardValue;
  }
  
  // 2. 레거시 경로들에서 찾기
  for (const legacyPath of mapping.legacy) {
    const legacyValue = getNestedValue(options, legacyPath);
    if (legacyValue !== undefined && legacyValue !== null) {
      return legacyValue;
    }
  }
  
  return undefined;
};

/**
 * 차트 타입별 모든 스타일 값 수집
 * @param {Object} options - 차트 옵션
 * @param {string} chartType - 차트 타입
 * @returns {Object} 수집된 스타일 값들
 */
export const collectAllStyleValues = (options, chartType) => {
  const mappings = LEGACY_STYLE_MAPPINGS[chartType] || {};
  const collectedStyles = {};
  
  Object.entries(mappings).forEach(([key, mapping]) => {
    const value = getStyleValue(options, chartType, key);
    if (value !== undefined && value !== null) {
      const standardPath = mapping.standard.replace('style.', '');
      setNestedValue(collectedStyles, standardPath, value);
    }
  });
  
  return collectedStyles;
};

/**
 * 스타일이 표준 형식인지 확인
 * @param {Object} options - 차트 옵션
 * @param {string} chartType - 차트 타입
 * @returns {boolean} 표준 형식 여부
 */
export const isStandardStyleFormat = (options, chartType) => {
  if (!options) return false;
  
  const mappings = LEGACY_STYLE_MAPPINGS[chartType] || {};
  
  // 레거시 속성이 있는지 확인
  for (const [key, mapping] of Object.entries(mappings)) {
    for (const legacyPath of mapping.legacy) {
      if (getNestedValue(options, legacyPath) !== undefined) {
        return false; // 레거시 속성이 있으면 표준 형식이 아님
      }
    }
  }
  
  // style 구조가 있는지 확인
  return !!options.style;
};

/**
 * 스타일 디버그 정보 출력
 * @param {Object} options - 차트 옵션
 * @param {string} chartType - 차트 타입
 */
export const debugStyleStructure = (options, chartType) => {
  console.group(`[Style Debug] Chart Type: ${chartType}`);
  
  // 표준 스타일 구조
  // 레거시 속성들
  // 최상위 레벨 속성들
  const topLevelKeys = ['fontSize', 'textColor', 'fontWeight', 'titleFontSize', 'badgeColor'];
  const topLevelProps = {};
  topLevelKeys.forEach(key => {
    if (options?.[key] !== undefined) {
      topLevelProps[key] = options[key];
    }
  });
  if (Object.keys(topLevelProps).length > 0) {
  }
  
  // 마이그레이션 필요 여부
  const needsMigration = !isStandardStyleFormat(options, chartType);
  console.groupEnd();
};